import { useContext, useEffect } from "react";
import PropTypes from "prop-types";

import Icon from "../icon/Icon";
import StudentInfoDao from "../../dao/StudentInfoDao";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/GuardianHome.scss";

/**
 * Return a student's picture, or if it doesn't exist, return a default placeholder
 * @name StudentImage
 * @param {string} className
 * @param {string} height
 * @param {{}} studentInfo
 * @param {string} width
 * @return {JSX.Element}
 */
const StudentImage = ({ className, height, noShift, setLoader, studentInfo, width }) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { eprStudentImage, schoolYearDto, token } = state || {};

    const schoolYearKey = schoolYearDto?.key;
    const studentNumber = studentInfo?.studentNumber;
    const cachedImageMatchesStudent =
        eprStudentImage != null && parseInt(eprStudentImage.studentNumber, 10) === parseInt(studentNumber, 10);
    /**
     * get the student's picture
     */
    useEffect(() => {
        // Prevents running if essential data is missing.
        if (!studentNumber || !schoolYearKey || !token) {
            return;
        }

        // Prevents running if we already have the correct image for the current student (Parent Segment / Cache Hit).
        if (cachedImageMatchesStudent) {
            return;
        }

        const fetchStudentImage = async () => {
            try {
                setLoader(true);
                const options = {
                    action: "studentImageRead",
                    ignoreError: true,
                    schoolYearKey,
                    studentNumber,
                    token
                };
                const response = await StudentInfoDao(options);
                if (response && response.data) {
                    const urlCreator = window.URL || window.webkitURL;
                    const url = urlCreator.createObjectURL(new Blob([response.data], { type: "image/jpeg" }));
                    const photoObj = {
                        studentNumber: studentNumber,
                        imageUrl: url
                    };
                    dispatch({
                        type: "EprStudentImage",
                        eprStudentImage: photoObj
                    });
                }
            } catch (error) {
                console.error(
                    `Unable to retrieve student image for ${studentInfo.firstName} ${studentInfo.lastName}:\n`,
                    error
                );
                const noPhotoObj = {
                    studentNumber: studentNumber,
                    imageUrl: null
                };
                dispatch({
                    type: "EprStudentImage",
                    eprStudentImage: noPhotoObj
                });
            } finally {
                setLoader(false);
            }
        };

        fetchStudentImage();
    }, [cachedImageMatchesStudent, dispatch, schoolYearKey, setLoader, studentNumber, studentInfo, token]);

    return (
        <div className="d-flex flex-row me-2">
            {cachedImageMatchesStudent && eprStudentImage.imageUrl ? (
                <img
                    alt={`${studentInfo.firstName} ${studentInfo.lastName}`}
                    className={className}
                    height={`${height}px`}
                    src={eprStudentImage.imageUrl}
                />
            ) : (
                <div
                    className="no-photo"
                    style={{
                        height: `${height}px`,
                        marginLeft: noShift ? "0" : "10px",
                        width: `${width}px`
                    }}
                >
                    <Icon iconName="NO_PHOTO" fill="#19608F" height={height} width={height} viewBox="296 0 768 1024" />
                </div>
            )}
        </div>
    );
};

StudentImage.propTypes = {
    className: PropTypes.string,
    height: PropTypes.number,
    noShift: PropTypes.bool,
    setLoader: PropTypes.func.isRequired,
    studentInfo: PropTypes.oneOfType([PropTypes.object]).isRequired,
    width: PropTypes.number
};

StudentImage.defaultProps = {
    className: "",
    height: 80,
    noShift: false,
    width: 80
};

export default StudentImage;
